// ============================================
// File: components/modals/EditInstructorModal.jsx
// Edit form for an Instructor (ServiceProvider) or Instructor Admin
// (ServiceProviderAdmin) record. Editable fields only — email, status,
// and role are intentionally excluded (see adminService.updateInstructorProfile).
// Shared by AdminList.jsx and InstructorList.jsx.
// ============================================

import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Save, AlertCircle, CheckCircle, X } from 'lucide-react';
import { adminAPI } from '../../services/api';

const EditInstructorModal = ({ instructor, onClose, onSaved }) => {
  const { getAccessTokenSilently } = useAuth0();

  const [formData, setFormData] = useState({
    firstName:    instructor.firstName    || '',
    lastName:     instructor.lastName     || '',
    phone:        instructor.phone        || '',
    businessName: instructor.businessName || '',
    address:      instructor.address      || '',
    postcode:     instructor.postcode     || '',
    status:       instructor.status       || 'active'
  });

  const [error, setError]     = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    if (error) setError('');
  };

  const handleSave = async () => {
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('First and last name are required');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const result = await adminAPI.updateInstructorProfile(
        instructor.instructorId,
        {
          firstName:    formData.firstName,
          lastName:     formData.lastName,
          phone:        formData.phone,
          businessName: formData.businessName,
          address:      formData.address,
          postcode:     formData.postcode,
          status:       formData.status
        },
        getAccessTokenSilently
      );

      if (!result.success) throw new Error(result.error || 'Failed to update');

      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to update instructor profile');
    } finally {
      setIsSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '0.55rem 0.75rem',
    border: '1.5px solid #e5e7eb', borderRadius: '0.5rem',
    fontSize: '0.9rem', fontFamily: 'inherit', outline: 'none'
  };
  const labelStyle = { fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem', display: 'block' };

  const roleLabel = instructor.role === 'ServiceProviderAdmin' ? 'Instructor Admin' : 'Instructor';

  return (
    <div className="template-modal-overlay">
      <div className="template-modal" style={{ maxWidth: '480px' }}>
        <div className="modal-header">
          <h3>Edit {roleLabel}</h3>
          <button className="modal-close" onClick={onClose} disabled={isSaving}>×</button>
        </div>

        <div className="template-list" style={{ padding: '0 1.5rem 1.5rem' }}>

          {error && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.6rem 0.85rem', marginBottom: '1rem',
              background: '#fef2f2', border: '1px solid #fca5a5',
              borderRadius: '0.4rem', fontSize: '0.82rem', color: '#991b1b'
            }}>
              <AlertCircle size={15} /> {error}
            </div>
          )}

          {/* ── Status ─────────────────────────────────────────────────── */}
          <div className="form-section" style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Status</label>
            <div className="status-toggle-group">
              {[
                { val: 'active',    label: 'Active' },
                { val: 'inactive',  label: 'Inactive' },
                { val: 'suspended', label: 'Suspended' }
              ].map(({ val, label: btnLabel }) => (
                <button key={val} type="button"
                  className={`status-toggle-btn ${formData.status === val ? val : ''}`}
                  onClick={() => setFormData(p => ({ ...p, status: val }))}>
                  {val === 'inactive' || val === 'suspended'
                    ? <X size={16} />
                    : <CheckCircle size={16} />}
                  {btnLabel}
                </button>
              ))}
            </div>
            <p className="form-hint" style={{ marginTop: '0.35rem', fontSize: '0.78rem', color: '#6b7280' }}>
              Current: <strong>{instructor.status?.charAt(0).toUpperCase() + instructor.status?.slice(1) || 'Active'}</strong>
            </p>
          </div>

          {/* Read-only email — not editable here */}
          <div className="form-group" style={{ marginBottom: '0.9rem' }}>
            <label style={labelStyle}>Email (login — not editable)</label>
            <input type="email" value={instructor.email} disabled
              style={{ ...inputStyle, background: '#f9fafb', color: '#9ca3af', cursor: 'not-allowed' }} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.9rem' }}>
            <div className="form-group">
              <label style={labelStyle}>First Name *</label>
              <input value={formData.firstName} onChange={handleChange('firstName')} disabled={isSaving} style={inputStyle} />
            </div>
            <div className="form-group">
              <label style={labelStyle}>Last Name *</label>
              <input value={formData.lastName} onChange={handleChange('lastName')} disabled={isSaving} style={inputStyle} />
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: '0.9rem' }}>
            <label style={labelStyle}>Phone</label>
            <input value={formData.phone} onChange={handleChange('phone')} disabled={isSaving} style={inputStyle} />
          </div>

          <div className="form-group" style={{ marginBottom: '0.9rem' }}>
            <label style={labelStyle}>Business Name</label>
            <input value={formData.businessName} onChange={handleChange('businessName')} disabled={isSaving} style={inputStyle} />
          </div>

          <div className="form-group" style={{ marginBottom: '0.9rem' }}>
            <label style={labelStyle}>Address</label>
            <input value={formData.address} onChange={handleChange('address')} disabled={isSaving} style={inputStyle} />
          </div>

          <div className="form-group" style={{ marginBottom: '0.9rem' }}>
            <label style={labelStyle}>Postcode</label>
            <input value={formData.postcode} onChange={handleChange('postcode')} disabled={isSaving} style={inputStyle} />
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} disabled={isSaving} className="btn-cancel">Cancel</button>
            <button type="button" onClick={handleSave} disabled={isSaving} className="btn-save"
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <Save size={15} /> {isSaving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditInstructorModal;